import { presetStore, settingStore, userStore, i18nStore } from '../../store'
import { Component, Show, createMemo, createSignal } from 'solid-js'
import { AutoPreset, getPresetOptions } from '../../shared/adapter'
import { A } from '@solidjs/router'
import { Card, SolidCard } from '/web/shared/Card'
import { PresetSelect } from '/web/shared/PresetSelect'
import TextInput from '/web/shared/TextInput'
import Button from '/web/shared/Button'
import { neat } from '/common/util'
import Modal, { HelpModal } from '/web/shared/Modal'
import { SetStoreFunction } from 'solid-js/store'
import { UserSettings } from './util'
import { FormLabel } from '/web/shared/FormLabel'
import Select from '/web/shared/Select'
import { EMBED_MODELS_OPTS } from '/web/store/embeddings'
import { Copy } from '/web/shared/Copy'
import { usePresetContext } from '/web/store/preset-context'
import { ModeGenSettings } from '/web/shared/Mode/ModeGenSettings'
import { Pencil } from 'lucide-solid'

const AISettings: Component<{
  state: UserSettings
  setter: SetStoreFunction<UserSettings>
}> = (props) => {
  const state = userStore((s) => ({ user: s.user, sub: s.sub, ui: s.ui }))
  const i18n = i18nStore((s) => ({ t: s.t }))
  const cfg = settingStore((s) => ({
    server: s.config.serverConfig,
  }))

  const presets = presetStore((s) => s.presets.filter((pre) => !!pre.service))
  const [summaryPreset, presetSetters] = usePresetContext({ anonymous: true })
  const [apiKey, setApiKey] = createSignal(state.user?.apiKey || '')
  const [editPreset, setEditPreset] = createSignal('')
  const [presetFooter, setPresetFooter] = createSignal<any>()

  const revealKey = async () => {
    const prev = apiKey()
    if (prev && !prev.includes('***')) return prev

    await userStore.revealApiKey((key) => {
      setApiKey(key)
    })

    return apiKey()
  }

  const generateKey = () => {
    userStore.generateApiKey((key) => {
      setApiKey(key)
    })
  }

  const presetOptions = createMemo(() => {
    const opts = getPresetOptions(presets, { builtin: true }).filter(
      (pre) => pre.value !== AutoPreset.chat && pre.value !== AutoPreset.service
    )
    return opts
  })

  const canUseApi = createMemo(() => {
    if (!cfg.server) return false
    if (!cfg.server.apiAccess || cfg.server?.apiAccess === 'off') return false
    if (!state.user?.admin) {
      if (cfg.server.apiAccess === 'users') return true
      return cfg.server.apiAccess === 'subscribers' && state.sub?.tier.apiAccess
    }

    return true
  })

  const openPreset = (id: string) => {
    setEditPreset(id)
    presetSetters.load(id)
  }

  return (
    <>
      <FormLabel
        label={
          <div class="flex flex-wrap items-center gap-1">
            <div>{i18n.t.settings_ai_embeddings}</div>
            <Select
              parentClass="text-sm py-1 px-2"
              items={EMBED_MODELS_OPTS}
              value={state.ui.embeddingModel || ''}
              onChange={(ev) => userStore.updateEmbeddingModel(ev.value)}
            />
          </div>
        }
        helperMarkdown={i18n.t.settings_ai_embeddingsHelper}
      />

      <Card>
        <div class="font-bold">{i18n.t.settings_ai_presets}</div>

        <div class="flex flex-col gap-2">
          <div class="flex items-end gap-2">
            <PresetSelect
              helperText={i18n.t.settings_ai_chargen}
              options={presetOptions()}
              selected={props.state.chargenPreset}
              setPresetId={(ev) => props.setter('chargenPreset', ev)}
              noneOption={{ label: i18n.t.settings_ai_useServerDefault, value: '' }}
            />
            <Button disabled={!props.state.chargenPreset}>
              <Pencil size={20} onClick={() => openPreset(props.state.chargenPreset || '')} />
            </Button>
          </div>

          <div class="flex items-end gap-2">
            <PresetSelect
              helperText={i18n.t.settings_ai_chatSummary}
              options={presetOptions()}
              selected={props.state.summaryPreset || state.user?.images?.summaryPresetId}
              setPresetId={(ev) => props.setter('summaryPreset', ev)}
              noneOption={{ label: i18n.t.settings_ai_useActiveChatPreset, value: '' }}
            />
            <Button disabled={!props.state.summaryPreset && !state.user?.images?.summaryPresetId}>
              <Pencil
                size={20}
                onClick={() =>
                  openPreset(props.state.summaryPreset || state.user?.images?.summaryPresetId || '')
                }
              />
            </Button>
          </div>

          <div class="flex items-end gap-2">
            <PresetSelect
              helperText={i18n.t.settings_ai_jsonSchemas}
              options={presetOptions()}
              selected={props.state.jsonPreset}
              setPresetId={(ev) => props.setter('jsonPreset', ev)}
              noneOption={{ label: i18n.t.settings_ai_useActiveChatPreset, value: '' }}
            />
            <Button disabled={!props.state.jsonPreset}>
              <Pencil size={20} onClick={() => openPreset(props.state.jsonPreset || '')} />
            </Button>
          </div>
        </div>
      </Card>

      <Show when={!canUseApi()}>
        <PresetSelect
          label={i18n.t.settings_ai_defaultPreset}
          helperText={i18n.t.settings_ai_defaultPresetHelper}
          options={presetOptions()}
          selected={props.state.defaultPreset}
          setPresetId={(ev) => props.setter('defaultPreset', ev)}
        />
      </Show>

      <Show when={canUseApi()}>
        <SolidCard class="flex flex-col gap-2">
          <HelpModal
            title={i18n.t.settings_ai_apiAccessTitle}
            cta={
              <div>
                <a class="link">{i18n.t.settings_ai_howToUseApi}</a>
              </div>
            }
            markdown={i18n.t.settings_ai_apiAccessHelp}
          />

          <PresetSelect
            fieldName="defaultPreset"
            label={i18n.t.settings_ai_apiAccessPreset}
            helperText={i18n.t.settings_ai_apiAccessPresetHelper}
            options={presetOptions()}
            selected={props.state.defaultPreset}
            setPresetId={(ev) => props.setter('defaultPreset', ev)}
          >
            <Show when={props.state.defaultPreset}>
              <Copy text={props.state.defaultPreset!} size={24} />
            </Show>
          </PresetSelect>

          <div class="flex items-center gap-1">
            <FormLabel label={i18n.t.settings_ai_apiKey} />
            <Show when={apiKey().includes('***')}>
              <Button size="pill" onClick={revealKey}>
                {i18n.t.settings_ai_revealKey}
              </Button>
            </Show>
            <Show when={apiKey() === 'Not set'}>
              <Button size="pill" onClick={generateKey}>
                {i18n.t.settings_ai_generateKey}
              </Button>
            </Show>
            <Show when={apiKey() !== 'Not set'}>
              <Button size="pill" onClick={generateKey}>
                {i18n.t.settings_ai_regenerateKey}
              </Button>
            </Show>
          </div>
          <div class="flex w-full items-center gap-1">
            <TextInput readonly placeholder={i18n.t.settings_ai_apiKeyHidden} value={apiKey()} />
            <Show when={apiKey() !== ''}>
              <Copy text={apiKey()} onClick={revealKey} size={24} />
            </Show>
          </div>
        </SolidCard>
      </Show>

      <div class="my-2">
        <SolidCard bg="premium-700" class="mb-2">
          {i18n.t.settings_ai_lookingForProviders}{' '}
          <A class="link font-bold" href="/presets?preset_tab=1">
            {i18n.t.settings_ai_headToPresets}
          </A>
        </SolidCard>
      </div>

      <Modal
        title={i18n.t.settings_ai_editSummaryPreset}
        show={!!editPreset()}
        close={() => setEditPreset('')}
        maxWidth="half"
        footer={presetFooter()}
      >
        <ModeGenSettings
          page="image-settings"
          preset={summaryPreset.current}
          setters={presetSetters}
          close={() => setEditPreset('')}
          presetId={editPreset()}
          onPresetChanged={() => setEditPreset('')}
          footer={setPresetFooter}
        />
      </Modal>
    </>
  )
}

export default AISettings
